import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { useGetReleaseKitQuery } from '../../../redux/api/fymAPI';
import { TypedMemo } from '../../common/types';
import { useOpenReleasePage } from '../../music24/promote/releases/useOpenReleasePage';
import { useRealtimePollInterval } from '../../realtime/useRealtimePollInterval';
import { stateMessages } from '../messages/stateMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The safety read of the render while the realtime socket is down. */
export const MAKING_POLL_MS = 10000;

interface MakingNoticeProps {
  accountId: number;
  catalogItemUuid: string;
  assetUuid: string;
  cutNumber: number;
  onDismiss: () => void;
}

/**
 * "Making the video · 34%" (design e3): the kit asset that "Make the video" queued, read from the
 * release kit of the song (`kit.asset.updated` reads it again; the poll runs only while the socket
 * is down). The user keeps editing; the render uses the cut as it was. "Go to the Kit" opens the
 * Kit tab of the release.
 */
const MakingNoticeComponent: React.FC<MakingNoticeProps> = ({
  accountId,
  catalogItemUuid,
  assetUuid,
  cutNumber,
  onDismiss,
}) => {
  const intl = useIntl();
  const openRelease = useOpenReleasePage({ accountId });
  const kit = useGetReleaseKitQuery({ accountId, catalogItemUuid });
  const asset = useMemo(
    () => [...(kit.data?.newest ?? []), ...(kit.data?.assets ?? [])].find((item) => item.uuid === assetUuid) ?? null,
    [assetUuid, kit.data]
  );
  const isDone = asset?.status === 'ready' || asset?.status === 'failed';
  const pollingInterval = useRealtimePollInterval(isDone ? 0 : MAKING_POLL_MS);
  useGetReleaseKitQuery({ accountId, catalogItemUuid }, { pollingInterval, skip: pollingInterval === 0 });

  const text =
    asset?.status === 'ready'
      ? intl.formatMessage(stateMessages.makingReady)
      : asset?.status === 'failed'
        ? intl.formatMessage(stateMessages.makingFailed, { credits: asset.credits })
        : asset !== null && asset.status === 'rendering'
          ? intl.formatMessage(stateMessages.making, { percent: Math.round(asset.progress), number: cutNumber })
          : intl.formatMessage(stateMessages.makingQueued, { number: cutNumber });

  return (
    <p className={clsx(styles.notice, asset?.status === 'failed' && styles.noticeWarn)} role="status">
      <span className={styles.noticeDot} />
      {text}
      <button type="button" className={styles.linkButton} onClick={() => openRelease(catalogItemUuid)}>
        {intl.formatMessage(stateMessages.goToKit)}
      </button>
      {isDone && (
        <button type="button" className={styles.linkButton} onClick={onDismiss}>
          {intl.formatMessage(stateMessages.dismiss)}
        </button>
      )}
    </p>
  );
};

export const MakingNotice = TypedMemo(MakingNoticeComponent);
